import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { BehaviorSubject, catchError, combineLatest, finalize, of, startWith, switchMap } from 'rxjs';
import { NotificationsService } from '@app/core/services/notifications.service';
import { Notificacion, NotificationFilter, NotificationsPage } from '../../interfaces/notification.interface';

@Component({
    selector: 'app-notification-inbox',
    standalone: true,
    imports: [CommonModule, PaginatorModule],
    templateUrl: './notification-inbox.component.html',
    styleUrl: './notification-inbox.component.scss'
})
export class NotificationInboxComponent implements OnInit {
    @Input() compact = false;
    @Output() viewAll = new EventEmitter<void>();
    private readonly service = inject(NotificationsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly query$ = new BehaviorSubject({ page: 1, limit: 20, estado: 'all' as NotificationFilter });
    readonly filters: { value: NotificationFilter; label: string }[] = [
        { value: 'all', label: 'Todas' },
        { value: 'unread', label: 'No leídas' },
        { value: 'read', label: 'Leídas' }
    ];
    data: NotificationsPage | null = null;
    loading = true;
    error = '';
    actionError = '';
    pending = new Set<number>();
    markingAll = false;
    get filter() { return this.query$.value.estado; }
    get first() { return this.data ? (this.data.page - 1) * this.data.limit : 0; }
    get last() { return this.data ? Math.min(this.first + this.data.items.length, this.data.total) : 0; }

    ngOnInit() {
        this.query$.next({ page: 1, limit: this.compact ? 8 : 20, estado: 'all' });
        this.service.userId$.pipe(
            switchMap(id => {
                this.data = null;
                this.actionError = '';
                this.pending.clear();
                this.query$.next({ page: 1, limit: this.compact ? 8 : 20, estado: 'all' });
                if (!id) {
                    this.loading = false;
                    return of(null);
                }
                return combineLatest([this.query$, this.service.changes$.pipe(startWith(undefined))]).pipe(
                    switchMap(([query]) => {
                        this.loading = true;
                        this.error = '';
                        return this.service.list(query.page, query.limit, query.estado).pipe(
                            catchError(() => {
                                this.error = 'No pudimos cargar tus notificaciones. Intentá nuevamente.';
                                return of(null);
                            })
                        );
                    })
                );
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(data => {
            this.data = data;
            this.loading = false;
        });
    }

    setFilter(estado: NotificationFilter) {
        this.query$.next({ ...this.query$.value, page: 1, estado });
    }

    changePage(event: PaginatorState) {
        this.query$.next({
            ...this.query$.value,
            page: (event.page ?? 0) + 1,
            limit: event.rows ?? this.query$.value.limit
        });
    }

    toggleRead(notification: Notificacion) {
        if (this.pending.has(notification.id) || this.markingAll) return;
        this.pending.add(notification.id);
        this.actionError = '';
        this.service.setRead(notification.id, !notification.visto).pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.pending.delete(notification.id))
        ).subscribe({
            error: () => { this.actionError = 'No se pudo cambiar el estado de lectura. Intentá nuevamente.'; }
        });
    }

    markAllRead() {
        if (this.markingAll || this.pending.size) return;
        this.markingAll = true;
        this.actionError = '';
        this.service.markAllRead().pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => { this.markingAll = false; })
        ).subscribe({
            error: () => { this.actionError = 'No se pudieron marcar las notificaciones como leídas. Intentá nuevamente.'; }
        });
    }

    refresh() { this.service.refresh(); }

    icon(type: string) {
        switch (type.toLowerCase()) {
            case 'ticket': return 'pi pi-comments';
            case 'planilla': return 'pi pi-clipboard';
            case 'observacion': return 'pi pi-eye';
            case 'gerenciamiento': return 'pi pi-car';
            default: return 'pi pi-bell';
        }
    }
}
